import React from "react";

/**
 * SectionWatermark - heritage emblem placed behind a section.
 * Reinecke/Tuch (2012): low VC + high PT = trust in 50ms.
 *
 * Colors hardcoded - `var(--brass)` doesn't reliably resolve in raw SVG
 * `fill=`/`stroke=` attributes. Stroke widths intentionally bold so the
 * emblem reads at low opacity.
 */

const BRASS_DEEP = "#7A5F2A";

export default function SectionWatermark({
  variant = "monogram",
  position = "right",
  size = 560,
  opacity = 0.16,
}) {
  const positionStyle = {
    right:        { top: "50%", right: "-8%", transform: "translateY(-50%)" },
    left:         { top: "50%", left: "-8%", transform: "translateY(-50%)" },
    center:       { top: "50%", left: "50%", transform: "translate(-50%, -50%)" },
    "top-right":  { top: "-12%", right: "-6%" },
    "top-left":   { top: "-12%", left: "-6%" },
    "bottom-right": { bottom: "-12%", right: "-6%" },
    "bottom-left":  { bottom: "-12%", left: "-6%" },
  }[position];

  return (
    <div
      aria-hidden="true"
      className="section-watermark"
      style={{
        position: "absolute",
        width: size,
        height: size,
        opacity,
        pointerEvents: "none",
        userSelect: "none",
        zIndex: 0,
        ...positionStyle,
      }}
    >
      {variant === "compass" ? <Compass /> : <Monogram />}
    </div>
  );
}

function Monogram() {
  const rays = Array.from({ length: 24 }, (_, i) => i * 15);
  return (
    <svg viewBox="0 0 200 200" width="100%" height="100%" fill="none">
      <g stroke={BRASS_DEEP} strokeWidth="1.4" fill="none">
        <circle cx="100" cy="100" r="96" />
        <circle cx="100" cy="100" r="92" />
        {rays.map((deg) => (
          <line
            key={deg}
            x1="100"
            y1="4"
            x2="100"
            y2="14"
            transform={`rotate(${deg} 100 100)`}
            strokeWidth="1.6"
          />
        ))}
        <circle cx="100" cy="100" r="68" strokeWidth="1.1" />
        <line x1="100" y1="20" x2="100" y2="34" strokeWidth="1" />
        <line x1="100" y1="166" x2="100" y2="180" strokeWidth="1" />
        <line x1="20" y1="100" x2="34" y2="100" strokeWidth="1" />
        <line x1="166" y1="100" x2="180" y2="100" strokeWidth="1" />
      </g>

      <text
        x="100"
        y="138"
        textAnchor="middle"
        fontSize="120"
        fontFamily="Cormorant Garamond, serif"
        fontStyle="italic"
        fontWeight="600"
        fill={BRASS_DEEP}
      >
        V
      </text>

      <defs>
        <path
          id="wm-circle"
          d="M 100,100 m -82,0 a 82,82 0 1,1 164,0 a 82,82 0 1,1 -164,0"
        />
      </defs>
      <text
        fontSize="9.5"
        fontFamily="JetBrains Mono, monospace"
        letterSpacing="3"
        fontWeight="600"
        fill={BRASS_DEEP}
      >
        <textPath href="#wm-circle" startOffset="0">
          VILORA · IMPEX · GUJARAT · 2025 · OF GRAIN OF SPICE OF TRUST ·
        </textPath>
      </text>
    </svg>
  );
}

function Compass() {
  return (
    <svg viewBox="0 0 200 200" width="100%" height="100%" fill="none">
      <g stroke={BRASS_DEEP} strokeWidth="1.4" fill="none">
        <circle cx="100" cy="100" r="96" />
        <circle cx="100" cy="100" r="92" />
        <circle cx="100" cy="100" r="58" strokeWidth="1.0" />

        {[0, 90, 180, 270].map((deg) => (
          <g key={`major-${deg}`} transform={`rotate(${deg} 100 100)`}>
            <polygon points="100,8 95,98 105,98" fill={BRASS_DEEP} stroke="none" opacity="0.65" />
            <polygon points="100,8 100,98 105,98" fill={BRASS_DEEP} stroke="none" />
          </g>
        ))}
        {[45, 135, 225, 315].map((deg) => (
          <g key={`minor-${deg}`} transform={`rotate(${deg} 100 100)`}>
            <polygon points="100,28 96,98 104,98" fill={BRASS_DEEP} stroke="none" opacity="0.5" />
          </g>
        ))}

        {Array.from({ length: 36 }, (_, i) => i * 10).map((deg) => (
          <line
            key={deg}
            x1="100"
            y1="4"
            x2="100"
            y2="12"
            transform={`rotate(${deg} 100 100)`}
            strokeWidth="0.9"
          />
        ))}

        <text x="100" y="22" textAnchor="middle" fontSize="13" fontFamily="JetBrains Mono, monospace" fontWeight="700" fill={BRASS_DEEP} stroke="none" letterSpacing="2">N</text>
        <text x="180" y="105" textAnchor="middle" fontSize="13" fontFamily="JetBrains Mono, monospace" fontWeight="700" fill={BRASS_DEEP} stroke="none" letterSpacing="2">E</text>
        <text x="100" y="188" textAnchor="middle" fontSize="13" fontFamily="JetBrains Mono, monospace" fontWeight="700" fill={BRASS_DEEP} stroke="none" letterSpacing="2">S</text>
        <text x="20" y="105" textAnchor="middle" fontSize="13" fontFamily="JetBrains Mono, monospace" fontWeight="700" fill={BRASS_DEEP} stroke="none" letterSpacing="2">W</text>

        <circle cx="100" cy="100" r="4" fill={BRASS_DEEP} stroke="none" />
      </g>
    </svg>
  );
}
